import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Slide,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
} from "@mui/material";
import {
  Event as EventIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useAuthStore } from "../store";
import { useTheme, useMediaQuery } from "@mui/material";

const StoreModal = ({ open, onClose, lessonCategoryId, lessonId, eventId, eventColor }) => {
  // Added lessonCategory as a prop
  const [availablePasses, setAvailablePasses] = useState([]);
  const [selectedPass, setSelectedPass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [addedPass, setAddedPass] = useState(null);
  const [acceptRegulamin, setAcceptRegulamin] = useState(false);

  const { user } = useAuthStore((state) => ({
    user: state.user,
  }));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (open) {
      fetchAvailablePasses();
    }
  }, [open]);

  // Helper function to check if a pass is unlimited
  const isUnlimitedPass = (pass) => {
    return pass.lesson_categories_for_pass_type?.some(
      (category) => category.is_unlimited === true
    );
  };

  // Helper function to check if a pass is an event pass
  const isEventPass = (pass) => {
    return pass.is_event_pass === true;
  };

  // Helper function to format event date
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Helper function to format duration in weeks
  const formatDuration = (validDuration) => {
    if (!validDuration) return null;
    
    const days = Math.floor(validDuration / 86400);
    const weeks = Math.floor(days / 7);
    
    if (days === 30) return "miesiąc";
    if (days === 90) return "3 miesiące";
    if (days === 365) return "rok";
    
    // Display in weeks if duration is a multiple of 7 days
    if (days % 7 === 0 && weeks > 0) {
      if (weeks === 1) return "tydzień";
      if (weeks === 2) return "2 tygodnie";
      if (weeks === 3) return "3 tygodnie";
      if (weeks === 4) return "4 tygodnie";
      return `${weeks} tygodni`;
    }
    
    // Fallback to days for non-week durations
    return `${days} dni`;
  };

  const fetchAvailablePasses = async () => {
    setLoading(true);
    const idToken = await user.getIdToken();
    try {
      let endpoint;
      
      // If eventId is provided, fetch only passes for that specific event
      if (eventId) {
        endpoint = `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/for-event/${eventId}`;
      } else {
        // Use the standard endpoint with availability info
        endpoint = `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/with-availability-info`;
      }
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await response.json();
      if (Array.isArray(data.objects)) {
        let passes = data.objects;

        // Filter passes by lesson category if provided (only for non-event passes, and not when fetching by eventId)
        if (lessonCategoryId && !eventId) {
          passes = passes.filter((pass) =>
            pass.is_event_pass || // Keep all event passes
            pass.lesson_categories_for_pass_type?.some(
              (lesson) => lesson.lesson_category_id === lessonCategoryId
            )
          );
        }

        // Filter out passes that are not available due to purchase limits
        passes = passes.filter(pass => 
          !pass.availability_info || pass.availability_info.is_available
        );

        setAvailablePasses(passes);
      } else {
        console.error("Invalid passes data format", data);
        setAvailablePasses([]);
      }
    } catch (error) {
      console.error("Failed to fetch available passes", error);
      // Fallback to original endpoint for backward compatibility
      try {
        const fallbackResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/available?respect_availability=true`,
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
        const fallbackData = await fallbackResponse.json();
        if (Array.isArray(fallbackData.objects)) {
          let passes = fallbackData.objects;

          if (lessonCategoryId) {
            passes = passes.filter((pass) =>
              pass.is_event_pass || // Keep all event passes
              pass.lesson_categories_for_pass_type?.some(
                (lesson) => lesson.lesson_category_id === lessonCategoryId
              )
            );
          }

          setAvailablePasses(passes);
        } else {
          setAvailablePasses([]);
        }
      } catch (fallbackError) {
        console.error("Fallback fetch also failed", fallbackError);
        setAvailablePasses([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (pass) => {
    setAddedPass(pass);
    setTimeout(() => {
      setSelectedPass(pass);
      setAddedPass(null);
    }, 300);
  };

  const handleRemoveFromCart = () => {
    setSelectedPass(null);
  };

  const handleSubmit = async () => {
    if (!acceptRegulamin) {
      alert("Proszę zaakceptować Regulamin.");
      return;
    }

    setPurchaseLoading(true);
    const idToken = await user.getIdToken();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/create_order`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pass_type_name: selectedPass.name,
            lesson_id: lessonId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        
        // Handle purchase limit exceeded error
        if (errorData && errorData.detail && typeof errorData.detail === 'object' && errorData.detail.error_type === 'purchase_limit_exceeded') {
          const { pass_type_name, current_count, limit } = errorData.detail;
          alert(`Limit zakupów został osiągnięty!\n\nKarnet "${pass_type_name}" ma limit ${limit} aktywnych karnetów.\nAktualnie aktywnych: ${current_count}\n\nSpróbuj ponownie później lub wybierz inny karnet.`);
        } else if (errorData && errorData.detail && typeof errorData.detail === 'string') {
          alert(`Błąd podczas zakupu: ${errorData.detail}`);
        } else {
          throw new Error("Failed to create order");
        }
        return;
      }

      const data = await response.json();
      const redirectUrl = data;

      // Show alert and then redirect
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Failed to create order", error);
      alert("Wystąpił błąd podczas procesu zakupu. Spróbuj ponownie.");
    } finally {
      setPurchaseLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "90%" : "50%", // Adjust width based on device type
          maxHeight: "80%",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Kup karnet
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {(() => {
              // Split passes into event passes, unlimited, and regular
              const eventPasses = availablePasses.filter(isEventPass);
              const unlimitedPasses = availablePasses.filter(pass => !isEventPass(pass) && isUnlimitedPass(pass));
              const regularPasses = availablePasses.filter(pass => !isEventPass(pass) && !isUnlimitedPass(pass));

              return (
                <Box style={{ maxHeight: "60vh", overflowY: "auto" }}>
                  {/* Event passes section */}
                  {eventPasses.length > 0 && (() => {
                    // Get section header color from eventColor prop or first event pass's event color
                    const sectionColor = eventColor || 
                      (eventPasses[0]?.events_for_pass_type?.[0]?.color) || 
                      '#ff9800';
                    return (
                      <Box mb={3}>
                        <Typography variant="h6" gutterBottom sx={{  fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon /> Karnety Eventowe
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Karnety na specjalne wydarzenia - warsztaty, retreaty i inne eventy
                        </Typography>
                        <Grid container spacing={2}>
                          {eventPasses.map((pass) => {
                            // Get color from the pass's first event, fallback to eventColor prop, then to default
                            const passColor = (pass.events_for_pass_type && pass.events_for_pass_type[0]?.color) || eventColor || '#ff9800';
                            // Get event date from first event
                            const firstEvent = pass.events_for_pass_type?.[0];
                            const eventDateDisplay = firstEvent?.start_date && firstEvent?.end_date
                              ? `${formatEventDate(firstEvent.start_date)} - ${formatEventDate(firstEvent.end_date)}`
                              : firstEvent?.start_date
                                ? formatEventDate(firstEvent.start_date)
                                : null;
                            return (
                            <Grid item xs={12} sm={6} key={pass.id}>
                              <Slide
                                direction={addedPass?.id === pass.id ? "left" : "up"}
                                in={true}
                                mountOnEnter
                                unmountOnExit
                                timeout={{ enter: 300, exit: 300 }}
                              >
                                <Card sx={{ border: '2px solid', borderColor: passColor, bgcolor: `${passColor}08` }}>
                                  <CardContent sx={{ textAlign: 'center' }}>
                                    <EventIcon sx={{ color: passColor, fontSize: 40, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{pass.name}</Typography>
                                    
                                    <Typography variant="h5" sx={{ color: passColor, fontWeight: 'bold', my: 1 }}>
                                      {pass.price} zł
                                    </Typography>
                                    
                                    {eventDateDisplay && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        📅 {eventDateDisplay}
                                      </Typography>
                                    )}
                                    
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 2 }}>
                                      🎫 Jeden karnet = wszystkie zajęcia wydarzenie!
                                    </Typography>

                                    {selectedPass && selectedPass.id === pass.id ? (
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleRemoveFromCart}
                                        sx={{ 
                                          bgcolor: passColor,
                                          '&:hover': { bgcolor: passColor, filter: 'brightness(0.9)' }
                                        }}
                                      >
                                        Usuń z koszyka
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleAddToCart(pass)}
                                        disabled={selectedPass !== null}
                                        sx={{ 
                                          bgcolor: passColor,
                                          '&:hover': { bgcolor: passColor, filter: 'brightness(0.9)' },
                                          '&:disabled': { bgcolor: `${passColor}50` }
                                        }}
                                      >
                                        Dodaj do koszyka
                                      </Button>
                                    )}
                                  </CardContent>
                                </Card>
                              </Slide>
                            </Grid>
                            );
                          })}
                        </Grid>
                        <Divider sx={{ my: 3 }} />
                      </Box>
                    );
                  })()}

                  {/* Unlimited passes section */}
                  {unlimitedPasses.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        ♾️ Karnety bez limitu
                      </Typography>
                      <Grid container spacing={2}>
                        {unlimitedPasses.map((pass) => (
                          <Grid item xs={12} sm={6} key={pass.id}>
                            <Slide
                              direction={addedPass?.id === pass.id ? "left" : "up"}
                              in={true}
                              mountOnEnter
                              unmountOnExit
                              timeout={{ enter: 300, exit: 300 }}
                            >
                              <Card sx={{ bgcolor: '#f5f5f5' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{pass.name}</Typography>
                                  
                                  <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold', my: 1 }}>
                                    {pass.price} zł
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    ♾️ Bez limitu wejść
                                  </Typography>

                                  {selectedPass && selectedPass.id === pass.id ? (
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      fullWidth
                                      onClick={handleRemoveFromCart}
                                    >
                                      Usuń z koszyka
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      fullWidth
                                      onClick={() => handleAddToCart(pass)}
                                      disabled={selectedPass !== null}
                                    >
                                      Dodaj do koszyka
                                    </Button>
                                  )}
                                </CardContent>
                              </Card>
                            </Slide>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Regular passes section */}
                  {regularPasses.length > 0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        🎫 Standardowe karnety
                      </Typography>
                      <Grid container spacing={2}>
                        {regularPasses.map((pass) => (
                          <Grid item xs={12} sm={6} key={pass.id}>
                            <Slide
                              direction={addedPass?.id === pass.id ? "left" : "up"}
                              in={true}
                              mountOnEnter
                              unmountOnExit
                              timeout={{ enter: 300, exit: 300 }}
                            >
                              <Card sx={{ bgcolor: '#fafafa' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{pass.name}</Typography>
                                  
                                  <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold', my: 1 }}>
                                    {pass.price} zł
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    ⏱️ Ważność: {formatDuration(pass.valid_duration)}
                                  </Typography>

                                  {selectedPass && selectedPass.id === pass.id ? (
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      fullWidth
                                      onClick={handleRemoveFromCart}
                                    >
                                      Usuń z koszyka
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      fullWidth
                                      onClick={() => handleAddToCart(pass)}
                                      disabled={selectedPass !== null}
                                    >
                                      Dodaj do koszyka
                                    </Button>
                                  )}
                                </CardContent>
                              </Card>
                            </Slide>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </>
        )}
        <Box
          mt={3}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptRegulamin}
                  onChange={(e) => setAcceptRegulamin(e.target.checked)}
                  name="acceptRegulamin"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Akceptuję{" "}
                  <a
                    href={`${process.env.PUBLIC_URL}/statute`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Regulamin
                  </a>
                </Typography>
              }
            />
            <Button variant="contained" color="error" onClick={onClose}>
              Anuluj
            </Button>
          </Box>
          <Box display="flex" justifyContent="flex-end" width="100%" mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={purchaseLoading || !selectedPass || !acceptRegulamin}
            >
              {purchaseLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Kup wybrany karnet"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default StoreModal;
